/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */

import { Category } from '../py-generated/datalouder/stock/types';

export type Region = 'hk' | 'us';
export type Language = 'zh' | 'en';

export type SignalConditionOperator = '<' | '<=' | '>=' | '>' | '=' | '==';

export type PriceChartPeriod = '1m' | '3m' | '6m' | '1y' | 'max';
export interface SignalCondition {
  field: string;
  operator: SignalConditionOperator;
  value: number;
}
export interface SignalOption {
  conditions: readonly SignalCondition[];
}

export interface DailyTicker {
  date: string;
  volume: number;
  open: number;
  close: number;
  high: number;
  low: number;
  turnover: number;
}

export interface Ticker {
  time: string;
  volume: number;
  open: number;
  close: number;
  high: number;
  low: number;
  turnover: number;
}

export interface StockSuggestionItem {
  stockCode: string;
  name_zh: string;
  name_en: string;
  stockType: string;
}

export interface FilerPage {
  ranking: string;
  lookup: string;
}

export interface Filter {
  us: FilerPage;
  hk: FilerPage;
}

export interface InfluencerSuggestionItem {
  influencerId: number;
  name: string;
}

export interface SourceLinkSuggestionItem {
  tipsSourceLink: string;
}

export type SuggestionItem =
  | StockSuggestionItem
  | InfluencerSuggestionItem
  | SourceLinkSuggestionItem;

export namespace Analytics {
  export enum ActionEnum {
    ClickHistoryPrices = 'ClickHistoryPrices',
    ClickNewsList = 'ClickNewsList',
    ClickCCASSSummary = 'ClickCCASSSummary',
    ViewNewsFile = 'ViewNewsFile',
    ViewCCASS = 'ViewCCASS',
    ViewCCASSSummary = 'ViewCCASSSummary',
    Like = 'Like',
    Dislike = 'Dislike',
    CancelLikes = 'CancelLikes',
    ChangePriceChartPreference = 'ChangePriceChartPreference',
    ChangePriceChartRange = 'ChangePriceChartRange',
    ViewCCASSHistory = 'ViewCCASSHistory',
    ViewParticipantHistory = 'ViewParticipantHistory',
    ArrowIntoHistoryPrices = 'ArrowIntoHistoryPrices',
    Lookup = 'Lookup',
    SelectSignal = 'SelectSignal',
    SelectSignalPermutation = 'SelectSignalPermutation',
    SelectGroupby = 'SelectGroupby',
    SelectGroup = 'SelectGroup',
    ExportTable = 'ExportTable',
    VisitPage = 'VisitPage',
    ViewUserFavList = 'ViewUserFavList', // Click a user in statistics user list dropdown
  }

  export interface Action {
    url: string;
    userId: number;
    vip: boolean;
    time: number;
    stockCode?: string;
    region: Region;
    action: ActionEnum;
    params?: unknown;
  }

  export type IdentifiableAction = Action & {
    id: string;
  };
}

export namespace Api {
  export interface DailyPricesSignalHistRequest {
    region: Region;
    signalId: number;
    permutation: string;
    groupBy: string; // TODO: use enum here
    group: number;
  }

  export interface DailyPricesSignalHistResponse {
    results: {
      date: number;
      count: number;
    }[];
    updatedAt: string;
  }

  export interface AnalyticsFlushRequest {
    actions: readonly Analytics.IdentifiableAction[];
  }
}

export interface LocalizedString {
  en: string;
  zh: string;
}

export interface Tutorial {
  id: number;
  TitleAndContent: {
    title: LocalizedString;
    body: LocalizedString;
  };
  children: ReadonlyArray<Tutorial>;
}

export interface Post {
  category: string;
  content: PostContent;
  date: string;
  id: number;
  popup: boolean;
  released: boolean;
  vipOnly: boolean;
  visiabletovisitor: boolean;
}

interface PostContent {
  id: string;
  title: PostContentTitle;
  body: PostContentBody;
}

interface PostContentTitle {
  id: number;
  en: string;
  zh: string;
}

interface PostContentBody {
  id: number;
  en: string;
  zh: string;
}

export interface ErrorResponseBody {
  error: string;
}

export interface CCASSStatisticItem {
  stockCode: string;
  bluestack: boolean;
  name_en: string;
  name_zh: string;
  close: number;
  open: number;
  closeBe1D: number;
  change1d: number;
  change3d: number;
  change5d: number;
  change1m: number;
  change1y: number;
  lowest52w: number;
  highest52w: number;
  lowest6m: number;
  highest6m: number;
  lowest3m: number;
  highest3m: number;
  lowest1m: number;
  highest1m: number;
  lowest5d: number;
  highest5d: number;
}

export interface CCASSStatisticsResponseBody {
  statistics: CCASSStatisticItem[];
  updatedAt: string;
}

interface IndustyInfo {
  id: string;
  name: {
    en: string;
    zh: string;
  };
}

export interface Stock {
  stockCode: string;
  name_en: string;
  name_zh: string;
  exchange: string;
  region: Region;
  isCryptoStock: boolean;
  stockType: string;
  category?: Category;
  industry?: [IndustyInfo, IndustyInfo];
}

export interface LookupRequestParameters {
  stockCode: string;
  category?: string;
}

export interface FetchAllStocksResponseBody {
  [key: string]: {
    id: string;
    ticker: string;
    name: {
      en: string;
      zh: string;
    };
    category: Category;
  };
}

export interface ListJobStatusesRequestParameters {
  jobId: number;
}

export interface JobStatus {
  status: string;
  time: string;
}

export interface ListJobStatusesResponseBody {
  statuses: JobStatus[];
}

export interface PaypalSubscription {
  subscriptionId: string;
  userId: number;
  status: string;
  raw: string;
}

export interface Tip {
  name: string;
  date: string;
  market: string;
  stockCode: string;
  direction: string;
  publicAlias?: string;
  alias: string;
  vip: boolean;
  price: number;
  channel?: string;
  sourceLink?: string;
  keyComment?: string;
  priceDate?: string; // tips' price dates are zoned market time in string
}

export enum TipsStatus {
  verified = 'V',
  approved = 'A',
  questionable = 'Q',
  doubted = 'D',
  deleted = 'X',
}

export interface USCompanyInfo {
  companyName: string;
  exchange: string;
  industry?: string;
  description?: string;
  ceo?: string;
  sector?: string;
}

export interface USKeyStats {
  sharesOutstanding: number;
  peRatio: number;
  beta: number;
  updatedAt: string;
}

export interface USFinancials {
  grossProfit?: number;
  totalRevenue?: number;
  netIncome?: number;
  currentAssets?: number;
  totalAssets?: number;
  totalLiabilities?: number;
  currentCash?: number;
  currentDebt?: number;
  totalCash?: number;
  totalDebt?: number;
  cashFlow?: number;
  updatedAt: string;
}

export interface USStockProfile {
  profile?: USCompanyInfo;
  keyStats?: USKeyStats;
  financial?: USFinancials;
}

export interface MembershipInfo {
  hasMembership: boolean;
  expireAt?: number; // timestamp in secs
  membershipSource?: string;
}

// Price 2.0

export const CATEGORIES: Category[] = ['US', 'HK', 'CRPT', 'CRPTBN'];

export interface BidAskItem {
  total_ask: number;
  total_ask_volume: number;
  total_bid: number;
  total_bid_volume: number;
  date: string;
}

export interface ConstituentSMAsItem {
  date: string;
  sma50_larger_price?: number;
  sma50_smaller_price?: number;
  sma150_larger_price?: number;
  sma150_smaller_price?: number;
  sma200_larger_price?: number;
  sma200_smaller_price?: number;
  updateTime?: number;
}

export namespace TimeSeries {
  export type Unit =
    | 'DAY'
    | 'MINUTE'
    | 'WEEK'
    | 'MONTH'
    | 'THREE_MINUTE'
    | 'FIVE_MINUTE'
    | 'FIFTEEN_MINUTE'
    | 'ONE_HOUR';

  export interface RequestParameters {
    data: string;
    unit: Unit;
    lastDate?: string;
  }

  export interface Item {
    dt: string;
  }
}

export type NOPStrategyResultResponse = {
  ticker: string;
  n_trades: number;
  win_rate: number;
  mean_annual_return: number;
  max_drawdown_rate: number;
  calmar_ratio: number;
  sharpe_ratio: number;
  sharpe_ratio_SH: number;
  total_return: number;
  total_return_SH: number;
}[];

export type NOPStrategyItems = {
  date: string;
  capital_sh: number;
  stategy_0_capital: number;
  stategy_1_capital: number;
  stategy_0_signal: string;
  stategy_1_signal: string;
  open: number;
};

export type NOPStrategySignalItem = {
  date: string;
  signal: Direction;
};

export type Direction =
  | 'Buy'
  | 'Hold'
  | 'Sell'
  | 'Flat Long Position'
  | 'Break_mdd';

export type NOPStrategySignal = {
  date: string;
  signal: string;
};

export interface Course {
  shortName: string;
  description: string;
  showAttendingMethods: boolean;
}

export interface statisticsHistoryRequestParameters {
  category: Category;
}

interface CCASSShareholdingCost {
  avgBuy: number;
  avgSell: number;
  avgTrade: number;
  totalChangeAmountByParticipant: number;
}
export interface CCASSShareholdingCostMap {
  [participant: string]: CCASSShareholdingCost;
}
